import { useState, useEffect } from "react";
import { Loader2 } from "lucide-react";
import axios from "axios";
import { PATCH_ORDER_URL } from "../../../features/UrlHelper";
import { Modal, Button } from "../../../components";
import PaymentForm, { toNumber, parseAmountInput, deriveReceivedAmount } from "../../../components/PaymentForm/PaymentForm";
import "./AdminEditDeliveredOrderModal.scss";

function AdminEditDeliveredOrderModal({ isOpen, onClose, order, onOrderUpdated }) {
  const [paymentType, setPaymentType] = useState("CASH");
  const [amountPaid, setAmountPaid] = useState("0");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState("");

  // Resetuj formulár pri otvorení modálu
  useEffect(() => {
    if (isOpen && order) {
      setPaymentType(order.payment_type || "CASH");
      // Realne prijata suma, nie orezany amount_paid -- inak by ulozenie bez
      // zmeny zrusilo splatku dlhu zaplatenu spolu s obedom.
      setAmountPaid(deriveReceivedAmount(order).toFixed(2));
      setError("");
    }
  }, [isOpen, order]);

  const handleClose = () => {
    if (!isSubmitting) {
      onClose();
    }
  };

  const handleSave = async () => {
    setError("");
    // toNumber() vracia pri nezmysle 0, takze stara kontrola isNaN() nikdy
    // nezabrala a prazdne pole sa ulozilo ako 0 € (cela hotovost do dlhu).
    const amountValue = paymentType === "CASH" ? parseAmountInput(amountPaid) : toNumber(amountPaid);

    if (amountValue === null) {
      setError("Zaplatená suma musí byť číslo väčšie alebo rovné 0 (napr. 6,90).");
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await axios.patch(
        PATCH_ORDER_URL(order.id),
        {
          payment_type: paymentType,
          amount_paid: amountValue,
          // Ked objednavku medzitym zmenil niekto iny (napr. vodic opravil
          // platbu), backend vrati 409 a suma sa neprepise naslepo.
          expected_version: order.version,
        }
      );

      onOrderUpdated(response.data);
      onClose();
    } catch (err) {
      console.error(err);
      setError(err.response?.data?.message || "Chyba pri ukladaní zmien.");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!order) return null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Úprava platobných údajov"
      footer={
        <>
          <Button
            variant="outline"
            onClick={handleClose}
            disabled={isSubmitting}
          >
            Zrušiť
          </Button>
          <Button
            variant="primary"
            onClick={handleSave}
            disabled={isSubmitting}
          >
            {isSubmitting && <Loader2 size={14} className="button-spinner" />}
            {isSubmitting ? "Ukladám..." : "Uložiť"}
          </Button>
        </>
      }
    >
      <div className="admin-edit-delivered-modal">
        {/* Infomácia o objednávke */}
        <div className="admin-edit-delivered-modal__info">
          <div className="admin-edit-delivered-modal__row">
            <span className="admin-edit-delivered-modal__label">Objednávka:</span>
            <strong className="admin-edit-delivered-modal__value">{order.order_code}</strong>
          </div>
          <div className="admin-edit-delivered-modal__row">
            <span className="admin-edit-delivered-modal__label">Zákazník:</span>
            <span className="admin-edit-delivered-modal__value">
              {[order.customer_name, order.customer_surname].filter(Boolean).join(" ") || "—"}
            </span>
          </div>
          <div className="admin-edit-delivered-modal__row">
            <span className="admin-edit-delivered-modal__label">Celková suma:</span>
            <strong className="admin-edit-delivered-modal__value">{Number(order.total_price || 0).toFixed(2)} €</strong>
          </div>
        </div>

        {/* Chybová správa */}
        {error && (
          <div className="admin-edit-delivered-modal__error">
            {error}
          </div>
        )}

        {/* Payment Form – rovnaká logika ako u vodiča v edit mode */}
        <PaymentForm
          paymentType={paymentType}
          setPaymentType={setPaymentType}
          amountPaid={amountPaid}
          setAmountPaid={setAmountPaid}
          order={order}
          isSubmitting={isSubmitting}
          isEditMode={true}
          showDriverNote={false}
        />
      </div>
    </Modal>
  );
}

export default AdminEditDeliveredOrderModal;
